import { useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Save } from "lucide-react";

export default function DistPasswordReset() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    if (f.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/dist/password-reset", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password updated. Use it on next login.");
      setF({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout title="Password Reset" subtitle="Update your login password securely">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-line p-6 lg:p-8 rounded-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 grid place-items-center bg-plum/10 border border-plum/25 rounded-sm">
              <KeyRound size={20} className="text-plum" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-royal">Change Password</h3>
              <p className="text-xs text-navy/60 mt-0.5">Choose a strong password (min. 8 characters, mix letters & numbers).</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 max-w-md">
            <PasswordField testid="pw-current" label="Current Password" show={show} value={f.current_password} onChange={(v) => setF({ ...f, current_password: v })} />
            <PasswordField testid="pw-new" label="New Password" show={show} value={f.new_password} onChange={(v) => setF({ ...f, new_password: v })} />
            <PasswordField testid="pw-confirm" label="Confirm New Password" show={show} value={f.confirm_password} onChange={(v) => setF({ ...f, confirm_password: v })} />
            <label className="text-xs text-navy/60 inline-flex items-center gap-2">
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="pw-show-toggle" />
              Show passwords
            </label>
          </div>
          <button data-testid="pw-submit" disabled={busy} className="mt-6 bg-royal text-white px-6 py-3 hover:bg-royal-deep rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
            <Save size={16} /> {busy ? "Updating..." : "Update Password"}
          </button>
        </form>

        <aside className="bg-white border border-line p-6 rounded-sm h-fit">
          <ShieldAlert className="text-rose" size={22} />
          <h4 className="font-serif text-lg text-royal mt-3">Security tips</h4>
          <ul className="mt-4 space-y-3 text-xs text-navy/70 leading-relaxed">
            <li>• Use a unique password not reused on other sites.</li>
            <li>• Longer is better — 12+ characters recommended.</li>
            <li>• Never share your password over WhatsApp or phone.</li>
            <li>• If you suspect compromise, change immediately.</li>
          </ul>
        </aside>
      </div>
    </DashboardLayout>
  );
}

function PasswordField({ label, value, onChange, show, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-navy/60">{label}</label>
      <input data-testid={testid} type={show ? "text" : "password"} required value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-line px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none" />
    </div>
  );
}
